import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody } from 'reactstrap';
import classNames from 'classnames/bind';
import { Calendar, ChevronDown } from 'lucide-react';
import Flatpickr from 'react-flatpickr';
import { Vietnamese } from 'flatpickr/dist/l10n/vn';
import 'flatpickr/dist/themes/material_blue.css';

import styles from './ModalAddEditVatService.module.scss';

const cx = classNames.bind(styles);

function ModalAddEditVatService(props) {
  const { isOpen, onToggle, onSubmit, itemData } = props;

  const isEdit = Boolean(itemData && itemData.id);

  const [taxType, setTaxType] = useState('Phí dịch vụ');
  const [customName, setCustomName] = useState('');
  const [calcType, setCalcType] = useState('amount'); // 'percent' | 'amount'
  const [rate, setRate] = useState('0');
  const [amountValue, setAmountValue] = useState('0');
  const [unitType, setUnitType] = useState('mỗi khách/đêm');
  const [effectiveDate, setEffectiveDate] = useState('Hôm nay');
  const [rateError, setRateError] = useState('');

  const [apiCalcType, setApiCalcType] = useState(null);
  const [apiRateValue, setApiRateValue] = useState(null);

  useEffect(() => {
    setRateError('');
    if (itemData && itemData.id) {
      if (itemData.name === 'Phí dịch vụ') {
        setTaxType('Phí dịch vụ');
        const match = itemData.rateInfo ? itemData.rateInfo.match(/(\d+(?:\.\d+)?)%/) : null;
        let rateVal = '0';
        if (match) {
          const [, matchedRate] = match;
          rateVal = matchedRate;
        } else if (itemData.value !== undefined && itemData.value !== null) {
          rateVal = String(itemData.value);
        }
        setRate(rateVal);
        setApiCalcType('percent');
        setApiRateValue(rateVal);
      } else {
        setTaxType('Thuế/phí khác');
        setCustomName(itemData.name || '');

        const rawFee = itemData.raw || {};
        const valueType = rawFee.valueType || itemData.valueType;
        const feeValue = rawFee.value ?? itemData.value ?? itemData.rate;

        const percentMatch = itemData.rateInfo ? itemData.rateInfo.match(/(\d+(?:\.\d+)?)\s*%/) : null;
        const amountMatch = itemData.rateInfo ? itemData.rateInfo.match(/([\d.]+)\s*VND/i) : null;

        let extractedRate = '';
        if (feeValue !== undefined && feeValue !== null && feeValue !== '') {
          extractedRate = String(feeValue);
        } else if (percentMatch) {
          [, extractedRate] = percentMatch;
        } else if (amountMatch) {
          [, extractedRate] = amountMatch;
        }

        let initialCalc = 'amount';
        if (valueType === 'PERCENT') {
          initialCalc = 'percent';
        } else if (valueType === 'AMOUNT') {
          initialCalc = 'amount';
        } else if (itemData.rateInfo && itemData.rateInfo.includes('VND')) {
          initialCalc = 'amount';
        } else if (itemData.rateInfo && itemData.rateInfo.includes('%')) {
          initialCalc = 'percent';
        } else if (itemData.calcType) {
          initialCalc = itemData.calcType;
        }

        const validRate = extractedRate || '0';
        setCalcType(initialCalc);
        setApiCalcType(initialCalc);
        setApiRateValue(validRate);

        if (initialCalc === 'percent') {
          setRate(validRate);
          setAmountValue('');
        } else {
          const num = Number(String(validRate).replace(/\D/g, ''));
          const formattedInit = (!Number.isNaN(num) && num > 0)
            ? num.toLocaleString('vi-VN')
            : (validRate || '');
          setAmountValue(formattedInit);
          setRate('0');
        }

        if (rawFee.chargeBasis) {
          switch (rawFee.chargeBasis) {
            case 'PER_NIGHT':
              setUnitType('mỗi phòng/đêm');
              break;
            case 'PER_PERSON_PER_NIGHT':
              setUnitType('mỗi khách/đêm');
              break;
            case 'PER_PERSON_PER_STAY':
              setUnitType('mỗi khách/lần lưu trú');
              break;
            case 'PER_STAY':
              setUnitType('mỗi phòng/lần lưu trú');
              break;
            default:
              setUnitType('mỗi khách/đêm');
              break;
          }
        } else if (itemData.unitType) {
          setUnitType(itemData.unitType);
        }
      }
      setEffectiveDate(itemData.effectiveDate || 'Hôm nay');
    } else {
      setTaxType('Thuế/phí khác');
      setCustomName('');
      setCalcType('amount');
      setApiCalcType(null);
      setApiRateValue(null);
      setRate('0');
      setAmountValue('');
      setUnitType('mỗi khách/đêm');
      setEffectiveDate('Hôm nay');
    }
  }, [itemData, isOpen]);

  const handleAmountChange = (e) => {
    const rawVal = e.target.value;
    const digitsOnly = rawVal.replace(/\D/g, '');
    if (!digitsOnly) {
      setAmountValue('');
      return;
    }
    const formatted = Number(digitsOnly).toLocaleString('vi-VN');
    setAmountValue(formatted);
  };

  const handleRateChange = (e) => {
    const rawVal = e.target.value.replace(',', '.');
    if (rawVal === '' || /^\d*\.?\d*$/.test(rawVal)) {
      const num = parseFloat(rawVal);
      if (!Number.isNaN(num) && num > 50) {
        setRate('50');
        setRateError('Tỷ lệ % không được vượt quá 50%');
      } else {
        setRate(rawVal);
        setRateError('');
      }
    }
  };

  const handleCalcTypeChange = (e) => {
    const newCalcType = e.target.value;
    setCalcType(newCalcType);

    if (newCalcType === 'percent') {
      if (apiCalcType === 'percent' && apiRateValue !== null) {
        setRate(apiRateValue);
      } else {
        setRate('0');
      }
    } else if (newCalcType === 'amount') {
      if (apiCalcType === 'amount' && apiRateValue !== null) {
        const num = Number(String(apiRateValue).replace(/\D/g, ''));
        const formattedInit = (!Number.isNaN(num) && num > 0)
          ? num.toLocaleString('vi-VN')
          : (apiRateValue || '');
        setAmountValue(formattedInit);
      } else {
        setAmountValue('');
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let nameStr = taxType;
    let rateInfoStr = '';

    if (taxType === 'Phí dịch vụ') {
      nameStr = 'Phí dịch vụ';
      rateInfoStr = `Đã bao gồm Phí dịch vụ khách sạn ${rate}% tính trên giá nett`;
    } else {
      nameStr = customName || 'Thuế/phí khác';
      const feeNameLower = customName ? customName.toLowerCase() : 'phí';
      if (calcType === 'amount') {
        const numericVal = Number(String(amountValue || '0').replace(/\./g, ''));
        const formattedAmount = !Number.isNaN(numericVal) && numericVal > 0
          ? numericVal.toLocaleString('vi-VN')
          : (amountValue || '0');
        rateInfoStr = `Thu tại khách sạn ${feeNameLower} ${formattedAmount} VND ${unitType}`;
      } else {
        rateInfoStr = `Thu tại khách sạn ${feeNameLower} ${rate}% ${unitType}`;
      }
    }

    onSubmit({
      id: isEdit ? itemData.id : Date.now().toString(),
      name: nameStr,
      rateInfo: rateInfoStr,
      effectiveDate: effectiveDate || 'Hôm nay',
      status: itemData?.status || 'applied',
      value: Number(rate),
      rate,
      amountValue,
      unitType,
      calcType,
    });
    onToggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onToggle}
      className={cx('Modal')}
      centered
    >
      <ModalBody className={cx('ModalBody')}>
        <div className={cx('ModalHeader')}>
          <h3 className={cx('Title')}>
            {isEdit ? 'Sửa thuế hoặc phí' : 'Thêm thuế hoặc phí'}
          </h3>
          <button
            type="button"
            aria-label="Đóng"
            className={cx('ButtonClose')}
            onClick={onToggle}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="12" cy="12" r="10" fill="#4B72EE" />
              <path
                d="M15 9L9 15M9 9L15 15"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Section 1: Loại thuế/phí */}
          <div className={cx('FormGroup')}>
            <span className={cx('Label')}>Loại thuế/phí</span>
            <div className={cx('RadioGroup')}>
              {isEdit && taxType === 'Phí dịch vụ' && (
                <label className={cx('RadioOption')} htmlFor="taxTypeService">
                  <input
                    type="radio"
                    id="taxTypeService"
                    name="taxType"
                    value="Phí dịch vụ"
                    checked={taxType === 'Phí dịch vụ'}
                    onChange={(e) => setTaxType(e.target.value)}
                    className={cx('RadioInput')}
                  />
                  <span className={cx('RadioText')}>Phí dịch vụ</span>
                </label>
              )}

              {(!isEdit || taxType === 'Thuế/phí khác') && (
                <label className={cx('RadioOption')} htmlFor="taxTypeOther">
                  <input
                    type="radio"
                    id="taxTypeOther"
                    name="taxType"
                    value="Thuế/phí khác"
                    checked={taxType === 'Thuế/phí khác'}
                    onChange={(e) => setTaxType(e.target.value)}
                    className={cx('RadioInput')}
                  />
                  <span className={cx('RadioText')}>Thuế/phí khác</span>
                </label>
              )}
            </div>

            <div className={cx('InfoBanner')}>
              {taxType === 'Phí dịch vụ'
                ? 'Phí dịch vụ bao gồm trong giá phòng và sẽ được hiển thị trên Bookese.'
                : 'Thuế/phí khác không bao gồm trong giá phòng và sẽ được thu tại khách sạn.'}
            </div>
          </div>

          {/* If "Thuế/phí khác" is selected */}
          {taxType === 'Thuế/phí khác' ? (
            <>
              {/* Tên khoản thu */}
              <div className={cx('FormGroup')}>
                <label htmlFor="customNameInput" className={cx('Label')}>
                  Tên khoản thu
                </label>
                <input
                  id="customNameInput"
                  type="text"
                  className={cx('InputControlFull')}
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="VD: Phí môi trường...."
                />
              </div>

              {/* Tính phí theo */}
              <div className={cx('FormGroup')}>
                <span className={cx('Label')}>Tính phí theo</span>
                <div className={cx('RadioGroup')}>
                  <label className={cx('RadioOption')} htmlFor="calcTypePercent">
                    <input
                      type="radio"
                      id="calcTypePercent"
                      name="calcType"
                      value="percent"
                      checked={calcType === 'percent'}
                      onChange={handleCalcTypeChange}
                      className={cx('RadioInput')}
                    />
                    <span className={cx('RadioText')}>Tỷ lệ %</span>
                  </label>

                  <label className={cx('RadioOption')} htmlFor="calcTypeAmount">
                    <input
                      type="radio"
                      id="calcTypeAmount"
                      name="calcType"
                      value="amount"
                      checked={calcType === 'amount'}
                      onChange={handleCalcTypeChange}
                      className={cx('RadioInput')}
                    />
                    <span className={cx('RadioText')}>Số tiền cụ thể</span>
                  </label>
                </div>
              </div>

              {/* Mức thu (%) / Mức thu */}
              <div className={cx('FormGroup')}>
                <label htmlFor="rateInputOther" className={cx('Label')}>
                  {calcType === 'amount' ? 'Mức thu' : 'Mức thu (%)'}
                </label>
                <div className={cx('AmountRow')}>
                  {calcType === 'amount' ? (
                    <div className={cx('InputSuffixGroup', 'AmountInputGroup')}>
                      <input
                        id="rateInputOther"
                        type="text"
                        className={cx('InputControl')}
                        value={amountValue}
                        onChange={handleAmountChange}
                        placeholder="Vui lòng nhập giá"
                      />
                      <span className={cx('Suffix')}>VNĐ</span>
                    </div>
                  ) : (
                    <div className={cx('InputSuffixGroup', 'AmountInputGroup')}>
                      <input
                        id="rateInputOther"
                        type="text"
                        className={cx('InputControl')}
                        value={rate}
                        onChange={handleRateChange}
                        placeholder="Vui lòng nhập tỷ lệ"
                      />
                      <span className={cx('Suffix')}>%</span>
                    </div>
                  )}

                  <div className={cx('SelectGroup')}>
                    <select
                      aria-label="Đơn vị tính phí"
                      className={cx('SelectControl')}
                      value={unitType}
                      onChange={(e) => setUnitType(e.target.value)}
                    >
                      <option value="mỗi khách/đêm">mỗi khách/đêm</option>
                      <option value="mỗi phòng/đêm">mỗi phòng/đêm</option>
                      <option value="mỗi phòng/lần lưu trú">mỗi phòng/lần lưu trú</option>
                      <option value="mỗi khách/lần lưu trú">mỗi khách/lần lưu trú</option>
                    </select>
                    <ChevronDown className={cx('DropdownIcon')} size={18} />
                  </div>
                </div>
                {calcType === 'percent' && rateError && (
                  <p className={cx('ErrorMessage')}>{rateError}</p>
                )}
              </div>
            </>
          ) : (
            /* Option "Phí dịch vụ" */
            <div className={cx('FormGroup')}>
              <label htmlFor="rateInputService" className={cx('Label')}>
                Mức thu (%)
              </label>
              <div className={cx('InputSuffixGroup')}>
                <input
                  id="rateInputService"
                  type="text"
                  className={cx('InputControl')}
                  value={rate}
                  onChange={handleRateChange}
                  placeholder="0"
                />
                <span className={cx('Suffix')}>%</span>
              </div>
              {rateError ? (
                <p className={cx('ErrorMessage')}>{rateError}</p>
              ) : (
                <p className={cx('HelperText')}>
                  Khoản thu này đã nằm trong giá phòng mà chỗ nghỉ cung cấp cho khách
                  hàng. Khách không phải thanh toán thêm.
                </p>
              )}
            </div>
          )}

          {/* Section: Áp dụng từ */}
          <div className={cx('FormGroup')}>
            <label htmlFor="effectiveDateInput" className={cx('Label')}>
              Áp dụng từ
            </label>
            <div className={cx('InputDateGroup')}>
              <Calendar className={cx('DateIcon')} size={18} />
              <Flatpickr
                id="effectiveDateInput"
                value={effectiveDate}
                onChange={(_, dateStr) => {
                  setEffectiveDate(dateStr);
                }}
                placeholder={taxType === 'Thuế/phí khác' ? 'Ngày bắt đầu' : 'Hôm nay'}
                options={{
                  dateFormat: 'd/m/Y',
                  locale: Vietnamese,
                }}
                className={cx('InputControlDate')}
              />
              <ChevronDown className={cx('DropdownIcon')} size={18} />
            </div>
            <p className={cx('HelperText')}>
              Áp dụng cho các đơn đặt phòng có ngày lưu trú từ ngày này.
            </p>
          </div>

          {/* Modal Footer */}
          <div className={cx('ModalFooter')}>
            <button
              type="button"
              className={cx('ButtonCancel')}
              onClick={onToggle}
            >
              Huỷ
            </button>
            <button type="submit" className={cx('ButtonSubmit')}>
              Xác nhận
            </button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
}

ModalAddEditVatService.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  itemData: PropTypes.instanceOf(Object),
};

ModalAddEditVatService.defaultProps = {
  itemData: null,
};

export default ModalAddEditVatService;
